import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LayoutDashboard, FileText, Settings, Users, History, LogOut, Plus, ChevronRight, Loader2, X } from 'lucide-react';
import { AuditLogView } from './NocodeAuditLogView';
import { UsersManagement } from './NocodeUsersManagement';
import { SimulatorDetailModal } from './NocodeSimulatorDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardNocode = () => {
  const { user, permissions, logout, token, hasPermission } = useAdminAuth();
  const [simulators, setSimulators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedSimulator, setSelectedSimulator] = useState(null);
  const [lang, setLang] = useState('fr');
  const [showCreateSimulator, setShowCreateSimulator] = useState(false);
  const [newSimulatorData, setNewSimulatorData] = useState({ name: '', key: '', description: '' });
  const [creating, setCreating] = useState(false);

  const t = {
    fr: {
      dashboard: "Tableau de bord",
      simulators: "Simulateurs",
      rules: "Règles",
      audit: "Audit",
      users: "Utilisateurs",
      settings: "Paramètres",
      logout: "Déconnexion",
      welcome: "Bienvenue",
      role: "Rôle",
      noSimulators: "Aucun simulateur",
      createSimulator: "Créer un simulateur",
      published: "Publié",
      draft: "Brouillon",
      versions: "versions",
      viewDetails: "Voir détails",
      lastPublished: "Dernière publication"
    },
    en: {
      dashboard: "Dashboard",
      simulators: "Simulators",
      rules: "Rules",
      audit: "Audit",
      users: "Users",
      settings: "Settings",
      logout: "Logout",
      welcome: "Welcome",
      role: "Role",
      noSimulators: "No simulators",
      createSimulator: "Create simulator",
      published: "Published",
      draft: "Draft",
      versions: "versions",
      viewDetails: "View details",
      lastPublished: "Last published"
    }
  };

  useEffect(() => {
    fetchSimulators();
  }, []);

  const fetchSimulators = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/simulators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSimulators(data.simulators || []);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSimulator = async () => {
    if (!newSimulatorData.name || !newSimulatorData.key) {
      alert('Le nom et la clé sont requis');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/simulators`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newSimulatorData.name,
          key: newSimulatorData.key.toLowerCase().replace(/[^a-z0-9_-]/g, '_'),
          description: newSimulatorData.description || ''
        })
      });
      
      if (res.ok) {
        const _data = await res.json();
        setShowCreateSimulator(false);
        setNewSimulatorData({ name: '', key: '', description: '' });
        fetchSimulators(); // Refresh list
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de créer le simulateur'}`);
      }
    } catch {
      alert('Erreur lors de la création du simulateur');
    } finally {
      setCreating(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      rule_editor: 'bg-blue-100 text-blue-800',
      approver: 'bg-green-100 text-green-800',
      publisher: 'bg-purple-100 text-purple-800'
    };
    const labels = {
      admin: 'Admin',
      rule_editor: 'Éditeur',
      approver: 'Approbateur',
      publisher: 'Publicateur'
    };
    return (
      <Badge className={colors[role] || 'bg-gray-700 text-white'}>
        {labels[role] || role}
      </Badge>
    );
  };

  const getStatusBadge = (simulator) => {
    if (simulator.published_version_id) {
      return <Badge className="bg-green-700 text-white font-semibold">✓ {t[lang].published}</Badge>;
    }
    return <Badge className="bg-yellow-600 text-white font-semibold">📝 {t[lang].draft}</Badge>;
  };

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: t[lang].dashboard },
    { id: 'simulators', icon: FileText, label: t[lang].simulators },
    { id: 'audit', icon: History, label: t[lang].audit, permission: 'can_view_audit' },
    { id: 'users', icon: Users, label: t[lang].users, permission: 'can_manage_users' },
    { id: 'settings', icon: Settings, label: t[lang].settings }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r shadow-sm flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold text-gray-800">O'SCOP Admin</h1>
          <p className="text-xs text-gray-500">Back-Office No-Code</p>
        </div>
        
        {/* User info */}
        <div className="p-4 border-b bg-gray-50">
          <p className="font-medium text-sm text-gray-800">{user?.name}</p>
          <p className="text-xs text-gray-500 mb-2">{user?.email}</p>
          {getRoleBadge(user?.role)}
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-2">
          {menuItems.map(item => {
            if (item.permission && !hasPermission(item.permission)) return null;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === item.id 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </nav>
        
        {/* Language & Logout */}
        <div className="p-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="w-full justify-start mb-1"
          >
            {lang === 'fr' ? '🇬🇧 English' : '🇫🇷 Français'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { logout(); window.location.href = '/'; }}
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t[lang].logout}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : activeTab === 'dashboard' || activeTab === 'simulators' ? (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {t[lang].welcome}, {user?.name?.split(' ')[0]}!
                </h2>
                <p className="text-gray-500">
                  {simulators.length} simulateur(s) configuré(s)
                </p>
              </div>
              {hasPermission('can_manage_simulators') && (
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowCreateSimulator(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t[lang].createSimulator}
                </Button>
              )}
            </div>

            {/* Simulators grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {simulators.map(sim => (
                <Card 
                  key={sim.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedSimulator(sim)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{sim.name}</CardTitle>
                        <p className="text-xs text-gray-500 font-mono">{sim.key}</p>
                      </div>
                      {getStatusBadge(sim)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {sim.description || 'Aucune description'}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {sim.versions_count || 0} {t[lang].versions}
                      </span>
                      <Button variant="ghost" size="sm" className="text-blue-600">
                        {t[lang].viewDetails}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {simulators.length === 0 && (
              <Card className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">{t[lang].noSimulators}</p>
                {hasPermission('can_manage_simulators') && (
                  <Button 
                    className="mt-4"
                    onClick={() => setShowCreateSimulator(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t[lang].createSimulator}
                  </Button>
                )}
              </Card>
            )}
          </div>
        ) : activeTab === 'audit' ? (
          <AuditLogView token={token} lang={lang} />
        ) : activeTab === 'users' ? (
          <UsersManagement token={token} lang={lang} />
        ) : (
          <div className="text-center py-12 text-gray-500">
            Section en construction...
          </div>
        )}
      </div>

      {/* Create Simulator Modal */}
      {showCreateSimulator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Créer un nouveau simulateur</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowCreateSimulator(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du simulateur *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Simulateur RCDS BATICO"
                  value={newSimulatorData.name}
                  onChange={(e) => setNewSimulatorData({...newSimulatorData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clé unique *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="Ex: rcds_batico"
                  value={newSimulatorData.key}
                  onChange={(e) => setNewSimulatorData({...newSimulatorData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_')})}
                />
                <p className="text-xs text-gray-500 mt-1">Lettres minuscules, chiffres et underscore uniquement</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Description du simulateur..."
                  rows={3}
                  value={newSimulatorData.description}
                  onChange={(e) => setNewSimulatorData({...newSimulatorData, description: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateSimulator(false)}
                  disabled={creating}
                >
                  Annuler
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleCreateSimulator}
                  disabled={creating || !newSimulatorData.name || !newSimulatorData.key}
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer le simulateur
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Simulator detail modal */}
      {selectedSimulator && (
        <SimulatorDetailModal 
          simulator={selectedSimulator}
          onClose={() => setSelectedSimulator(null)}
          token={token}
          lang={lang}
          permissions={permissions}
          userRole={user?.role}
        />
      )}
    </div>
  );
};

// Audit Log Component

export default AdminDashboardNocode;
